import React from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/shared/SelectField';
import { SelectItem } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { DashboardFilters } from '../../services/dashboardService';

/** Radix Select has no empty option value, so "no filter" needs a sentinel. */
const ANY = '__any__';

/** The longest range the inventory activity endpoint accepts (DTO-enforced). */
export const MAX_RANGE_DAYS = 366;

export type AnalyticsFilterField =
    | 'dateRange' | 'departmentId' | 'locationId' | 'categoryId'
    | 'brandId' | 'vendorId' | 'status';

/** Filter state the drawer edits — `displayCurrency` is the page's, not the user's. */
export type AnalyticsFilterState = Omit<DashboardFilters, 'displayCurrency' | 'warrantyBucket' | 'usefulLifeBucket'>;

export const EMPTY_ANALYTICS_FILTERS: AnalyticsFilterState = {};

/**
 * Which filters each tab offers.
 *
 * Not a design preference — this mirrors what the tab's endpoints actually
 * honour, verified against the running API. Assets and licences carry no
 * department, location or category column, so those filters would change
 * nothing there; offering them would be a control that silently does nothing.
 * (Until this was fixed server-side they did worse than nothing: the query
 * referenced a column that does not exist and the endpoint returned 500.)
 */
export const TAB_FILTER_FIELDS: Record<string, AnalyticsFilterField[]> = {
    executive: ['dateRange', 'categoryId', 'brandId', 'vendorId'],
    assets: ['dateRange', 'status', 'brandId', 'vendorId'],
    licenses: ['dateRange', 'vendorId'],
    inventory: ['dateRange', 'categoryId', 'departmentId', 'locationId', 'brandId', 'vendorId'],
};

/** Only the fields this tab offers count towards the "Filters (3)" badge. */
export function countActiveAnalyticsFilters(
    filters: AnalyticsFilterState,
    fields: AnalyticsFilterField[],
): number {
    let count = 0;
    if (fields.includes('dateRange') && (filters.startDate || filters.endDate)) count += 1;
    (['departmentId', 'locationId', 'categoryId', 'brandId', 'vendorId', 'status'] as const)
        .forEach((field) => {
            if (fields.includes(field) && filters[field] !== undefined && filters[field] !== '') {
                count += 1;
            }
        });
    return count;
}

/** Days between two ISO dates, or null when the range is incomplete. */
export function rangeDays(startDate?: string, endDate?: string): number | null {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    return Math.round((end - start) / 86400000);
}

export interface AnalyticsFilterOption { id: number | string; name: string }

interface AnalyticsAdvancedFiltersProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Which tab's field set to render. */
    fields: AnalyticsFilterField[];
    /** Tab name, for the drawer subtitle. */
    tabLabel: string;
    draft: AnalyticsFilterState;
    onDraftChange: (filters: AnalyticsFilterState) => void;
    onApply: () => void;
    onClear: () => void;
    departmentOptions: AnalyticsFilterOption[];
    locationOptions: AnalyticsFilterOption[];
    categoryOptions: AnalyticsFilterOption[];
    brandOptions: AnalyticsFilterOption[];
    vendorOptions: AnalyticsFilterOption[];
    statusOptions: string[];
}

/**
 * Advanced-filter drawer for the Analytics tabs (User Story 101).
 *
 * Same shell as the Assets, Licences, Inventory and Cloud list pages — a Sheet
 * with grouped fields and an Apply/Clear footer — so the control behaves the
 * same wherever it appears. Fields are single-value rather than the list
 * pages' MultiSelect because the dashboard endpoints take one id per
 * dimension; a multi-select that quietly sent only the first choice would look
 * like it worked.
 */
export const AnalyticsAdvancedFilters: React.FC<AnalyticsAdvancedFiltersProps> = ({
    open, onOpenChange, fields, tabLabel, draft, onDraftChange, onApply, onClear,
    departmentOptions, locationOptions, categoryOptions, brandOptions, vendorOptions, statusOptions,
}) => {
    const set = <K extends keyof AnalyticsFilterState>(key: K, value: AnalyticsFilterState[K]) => {
        onDraftChange({ ...draft, [key]: value });
    };

    const setId = (key: 'departmentId' | 'locationId' | 'categoryId' | 'brandId' | 'vendorId', value: string) => {
        set(key, value === ANY ? undefined : Number(value));
    };

    const days = rangeDays(draft.startDate, draft.endDate);
    const rangeTooLong = days !== null && days > MAX_RANGE_DAYS;
    const rangeInverted = days !== null && days < 0;
    const rangeError = rangeInverted
        ? 'End date is before the start date.'
        : rangeTooLong
            ? `Ranges are limited to ${MAX_RANGE_DAYS} days — this one is ${days}.`
            : null;

    const idField = (
        key: 'departmentId' | 'locationId' | 'categoryId' | 'brandId' | 'vendorId',
        label: string,
        placeholder: string,
        options: AnalyticsFilterOption[],
        help?: string,
    ) => (
        // A dropdown whose only entry is "Any …" is a dead control — it appears
        // when the master list is empty or the user lacks permission to read it.
        options.length === 0 ? null : (
        <div className="space-y-1.5">
            <Label htmlFor={`afilter-${key}`}>{label}</Label>
            <SelectField
                id={`afilter-${key}`}
                value={draft[key] === undefined ? ANY : String(draft[key])}
                onValueChange={(v) => setId(key, v)}
                placeholder={placeholder}
            >
                <SelectItem value={ANY}>{placeholder}</SelectItem>
                {options.map((option) => (
                    <SelectItem key={option.id} value={String(option.id)}>{option.name}</SelectItem>
                ))}
            </SelectField>
            {help && <p className="text-xs text-muted-foreground">{help}</p>}
        </div>
        )
    );

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>Filter {tabLabel}</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-6 px-6 py-5">
                        {fields.includes('dateRange') && (
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold">Period</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="afilter-start">From</Label>
                                        <Input
                                            id="afilter-start" type="date"
                                            value={draft.startDate ?? ''}
                                            onChange={(e) => set('startDate', e.target.value || undefined)}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="afilter-end">To</Label>
                                        <Input
                                            id="afilter-end" type="date"
                                            value={draft.endDate ?? ''}
                                            onChange={(e) => set('endDate', e.target.value || undefined)}
                                        />
                                    </div>
                                </div>
                                {rangeError && <p className="text-xs text-destructive">{rangeError}</p>}
                                <p className="text-xs text-muted-foreground">
                                    Filters on when a record was created. Both dates are needed for the
                                    range to apply.
                                </p>
                            </div>
                        )}

                        {(fields.includes('categoryId') || fields.includes('departmentId') || fields.includes('locationId')) && (
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold">Organisation</h4>
                                {fields.includes('categoryId') && idField('categoryId', 'Category', 'Any category', categoryOptions)}
                                {fields.includes('departmentId') && idField(
                                    'departmentId', 'Department', 'Any department', departmentOptions,
                                    'Applies to issue/return activity, which is where a department is recorded.',
                                )}
                                {fields.includes('locationId') && idField(
                                    'locationId', 'Location', 'Any location', locationOptions,
                                    'Applies to issue/return activity, which is where a location is recorded.',
                                )}
                            </div>
                        )}

                        {(fields.includes('brandId') || fields.includes('vendorId') || fields.includes('status')) && (
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold">Supply & state</h4>
                                {fields.includes('brandId') && idField('brandId', 'Brand', 'Any brand', brandOptions)}
                                {fields.includes('vendorId') && idField('vendorId', 'Vendor', 'Any vendor', vendorOptions)}
                                {fields.includes('status') && (
                                    <div className="space-y-1.5">
                                        <Label htmlFor="afilter-status">Status</Label>
                                        <SelectField
                                            id="afilter-status"
                                            value={draft.status ?? ANY}
                                            onValueChange={(v) => set('status', v === ANY ? undefined : v)}
                                            placeholder="Any status"
                                        >
                                            <SelectItem value={ANY}>Any status</SelectItem>
                                            {statusOptions.map((status) => (
                                                <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                                            ))}
                                        </SelectField>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </ScrollArea>

                <SheetFooter>
                    <Button variant="outline" onClick={onClear} className="cursor-pointer">Clear all</Button>
                    <Button
                        onClick={() => { onApply(); onOpenChange(false); }}
                        disabled={!!rangeError}
                        className="cursor-pointer"
                    >
                        Apply Filters
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
